/**
 * Friendly display-name derivation (presentation layer).
 *
 * The SSO bridge from FOCTTA Core has no separate name claim, so the
 * Discovery session `displayName` is currently the user's email
 * (api-controller bridge service: `displayName: claims.email`). Rendering
 * that raw value in a greeting reads as
 *   "Welcome back, arjun.rao@example.com."
 *
 * The canonical identity stays the email (we genuinely only know that).
 * This helper just presents it nicely from whatever we have:
 *   - a real name (has a space, no '@')   -> used as-is
 *   - an email / dotted local part        -> "arjun.rao@x.com" -> "Arjun Rao"
 *
 * Independence note: this is a frontend-only nicety. It works the same
 * whether the issuer is Core, customer Okta/Azure AD, or Discovery's own
 * JWKS - anything that hands us an email-shaped displayName.
 */

function titleCase(segment: string): string {
  if (!segment) return segment;
  return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
}

/**
 * Derive a "Arjun Rao" style name from an email local-part.
 * Splits on '.', '_', '-'; drops purely-numeric segments
 * ("arjun.123" -> "Arjun"). Returns null when nothing usable remains.
 */
function nameFromEmailLocalPart(local: string): string | null {
  const segments = local
    .split(/[._-]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s) => !/^\d+$/.test(s));
  if (segments.length === 0) return null;
  return segments.map(titleCase).join(' ');
}

export interface FriendlyName {
  /** "Arjun Rao" for chrome / menus, or null if undeterminable. */
  full: string | null;
  /** "Arjun" for greetings, or null if undeterminable. */
  first: string | null;
}

/**
 * Best human-friendly full + first name for greetings and chrome, given a
 * (possibly email-shaped) displayName and email.
 */
export function friendlyName(
  displayName: string | null | undefined,
  email: string | null | undefined,
): FriendlyName {
  const dn = (displayName ?? '').trim();

  // A real name: non-empty and not an email address.
  if (dn && !dn.includes('@')) {
    const first = dn.split(/\s+/)[0] ?? dn;
    return { full: dn, first };
  }

  // Otherwise derive from the email (or an email-shaped displayName).
  const src =
    email && email.includes('@') ? email : dn.includes('@') ? dn : null;
  if (src) {
    const local = src.split('@')[0] ?? '';
    const full = nameFromEmailLocalPart(local);
    if (full) return { full, first: full.split(/\s+/)[0] ?? full };
  }

  return { full: null, first: null };
}
